/**
 * Abschluss — angebotene gegen abrechenbare Leistung.
 *
 * Das Werkzeug für die Schlussrechnung: links steht, was beauftragt war, rechts
 * trägst du ein, was wirklich abzurechnen ist. Daneben, was das System weiß —
 * gelieferte Mengen, Stunden, Fotos. Am Ende entsteht daraus der Entwurf.
 *
 * **Die Menge kommt von dir, nicht aus einer Ableitung.** Im Garten- und
 * Landschaftsbau ist das Aufmaß eine Messung vor Ort; ein vorbelegter Wert aus
 * unsicherer Zuordnung würde übersehen und mitabgerechnet. Vorgeschlagen wird
 * deshalb die angebotene Menge — sichtbar als „noch nicht aufgemessen".
 */
import { useState } from "react";
import {
  useAufmassSetzen,
  useLieferungZuordnen,
  useNachtragAnlegen,
  useNachtragLoeschen,
  usePositionErgaenzen,
  useProjektAbschluss,
  useSchlussrechnungEntwurf,
} from "../../hooks/usePlanung";
import type { Lieferposition } from "../../hooks/usePlanung";
import { errorText } from "../../lib/invoices";
import { formatEur } from "../../lib/planer";

const PHASE_LABEL: Record<string, string> = {
  bestandsaufnahme: "Vorher", verlauf: "Verlauf", ergebnis: "Nachher", sonstiges: "Sonstiges",
};

/** 12 statt 12,00 — aber 12,5 bleibt 12,5. */
function mengeText(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString("de-DE", { maximumFractionDigits: 3 });
}

/** Deutsche Eingabe: Komma zählt wie Punkt. Leer heißt „nicht aufgemessen". */
function alsZahl(roh: string): number | null {
  const t = roh.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export default function Projektabschluss({ projektId, aktiv }: { projektId: string; aktiv: boolean }) {
  const { data, isLoading, isError, error } = useProjektAbschluss(projektId, aktiv);
  const setzen = useAufmassSetzen(projektId);
  const entwurf = useSchlussrechnungEntwurf(projektId);
  const zuordnen = useLieferungZuordnen(projektId);
  const [belegeOffen, setBelegeOffen] = useState(false);

  if (!aktiv) return null;
  if (isLoading) return <div className="vg-detail-section-hint">Gegenüberstellung wird zusammengetragen …</div>;
  if (isError) return <div className="vg-detail-section-hint">{errorText(error)}</div>;
  if (!data) return null;

  const g = data.groessen;
  const gg = data.gegenueberstellung;
  const hatZeilen = gg.zeilen.length > 0 || gg.nachtraege.length > 0;

  return (
    <div className="vg-unterblock">
      {data.luecken.length > 0 && (
        <div className="vg-banner is-warn" style={{ marginBottom: 12 }}>
          <b>Womit gerechnet wurde — und womit nicht:</b>
          <ul>{data.luecken.map((l) => <li key={l}>{l}</li>)}</ul>
        </div>
      )}

      <div className="vg-kennzahlen">
        <div>
          <span>Angeboten netto</span>
          <strong>{formatEur(gg.summe_angeboten_netto || g.auftragswert_netto)}</strong>
          <small>{data.angebot.nummer || (g.auftragswert_quelle === "angebot" ? "Angebot" : "Projektwert")}</small>
        </div>
        <div>
          <span>Abrechenbar netto</span>
          <strong>{formatEur(g.abzurechnen_netto)}</strong>
          <small>
            {gg.summe_nachtraege_netto > 0 ? `davon ${formatEur(gg.summe_nachtraege_netto)} Nachträge` : "ohne Nachträge"}
          </small>
        </div>
        <div>
          <span>Noch zu stellen</span>
          <strong>{formatEur(g.rest_rechnerisch_netto)}</strong>
          <small>nach {formatEur(g.abschlaege_netto)} Abschlägen</small>
        </div>
      </div>

      {/* Die Gegenüberstellung */}
      <div className="vg-doku-gruppe">
        <div className="vg-doku-gruppe-titel">
          Angeboten gegen abrechenbar
          {gg.offene_positionen > 0 && (
            <span className="vg-proj-tab-count">{gg.offene_positionen} offen</span>
          )}
          {(gg.abweichend_von_lieferung ?? 0) > 0 && (
            <span className="vg-proj-tab-count is-lieferung">
              {gg.abweichend_von_lieferung} weich{gg.abweichend_von_lieferung === 1 ? "t" : "en"} von der Lieferung ab
            </span>
          )}
        </div>
        {gg.zeilen.length ? (
          <table className="k-cardtable vg-gegenueber">
            <thead>
              <tr>
                <th>Position</th><th>Angeboten</th><th>Abrechenbar</th><th>Differenz</th><th>Notiz</th>
              </tr>
            </thead>
            <tbody>
              {gg.zeilen.map((z) => (
                <AufmassZeile key={z.position_id} zeile={z} speichern={setzen.mutate} />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="vg-detail-section-hint">
            Dieses Angebot trägt nur eine Summe, keine einzelnen Positionen. Trag unten ein,
            was beauftragt war — danach steht die Gegenüberstellung wie überall.
          </div>
        )}
        {setzen.isError && <div className="vg-banner is-warn">{errorText(setzen.error)}</div>}
        <PositionErgaenzen projektId={projektId} />
      </div>

      <Nachtraege projektId={projektId} liste={gg.nachtraege} />

      {/* Was das System weiß — die Belege für die Entscheidung. Zugeklappt: es
          ist Beiwerk zur Eingabe oben, nicht die Hauptsache. */}
      <div className="vg-doku-gruppe">
        <button type="button" className="vg-btn" onClick={() => setBelegeOffen((z) => !z)}>
          {belegeOffen ? "▾" : "▸"} Belege dazu — {data.lieferungen.length} Lieferpositionen,{" "}
          {data.arbeit.termine} Termine
        </button>
        {zuordnen.isError && <div className="vg-banner is-warn">{errorText(zuordnen.error)}</div>}
        {belegeOffen && (
          <>
            {data.lieferungen.length > 0 && (
              <table className="k-cardtable" style={{ marginTop: 8 }}>
                <thead><tr><th>Geliefertes Material</th><th>Menge</th><th>Gehört zu</th></tr></thead>
                <tbody>
                  {data.lieferungen.map((l, i) => (
                    <tr key={`${l.material}-${l.einheit}-${l.position_id}-${i}`}>
                      <td data-label="Material">
                        {l.material}
                        {!l.im_katalog && <span className="vg-doku-hinweis"> · nicht im Katalog</span>}
                        {/* Abgeleitet heißt: das Material steht im übernommenen
                            Angebot an genau dieser Position. Sichtbar, damit
                            klar ist, dass hier niemand von Hand gewählt hat. */}
                        {l.zuordnung === "material" && (
                          <span className="vg-doku-hinweis"> · über das Material zugeordnet</span>
                        )}
                      </td>
                      <td data-label="Menge">{mengeText(l.menge)} {l.einheit}</td>
                      <td data-label="Gehört zu">
                        {/* Von Hand, nie geraten: „Rindenmulch 0-40mm" auf
                            „Beetpflege inkl. Mulchen" zu schließen hat schon
                            einmal ein fremdes Material angehängt. */}
                        <select
                          className="vg-aufmass-notiz"
                          value={l.position_id}
                          disabled={zuordnen.isPending}
                          onChange={(e) => zuordnen.mutate({ zeilen_ids: l.zeilen_ids, position_id: e.target.value })}
                          aria-label={`Angebotsposition für ${l.material}`}
                        >
                          <option value="">— nicht zugeordnet —</option>
                          {gg.zeilen.map((z) => (
                            <option key={z.position_id} value={z.position_id}>{z.titel}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="vg-detail-section-hint" style={{ marginTop: 8 }}>
              {data.arbeit.termine} Termine
              {data.arbeit.stunden > 0 && ` · ${mengeText(data.arbeit.stunden)} Stunden`}
              {data.arbeit.erster_tag && ` · ${data.arbeit.erster_tag} bis ${data.arbeit.letzter_tag}`}
              {" · Fotos: "}
              {Object.entries(data.arbeit.fotos).map(([p, n]) => `${n} ${PHASE_LABEL[p] || p}`).join(" · ") || "keine"}
            </div>
            {data.abschlaege.length > 0 && (
              <table className="k-cardtable" style={{ marginTop: 8 }}>
                <thead><tr><th>Beleg</th><th>Art</th><th>Netto</th><th>Status</th></tr></thead>
                <tbody>
                  {data.abschlaege.map((a) => (
                    <tr key={a.nummer}>
                      <td data-label="Beleg">{a.nummer}</td>
                      <td data-label="Art">{a.art}</td>
                      <td data-label="Netto">{formatEur(a.netto)}</td>
                      <td data-label="Status">{a.bezahlt_am ? `bezahlt ${a.bezahlt_am}` : a.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Der Ausgang */}
      <div className="vg-abschluss-fuss">
        <div>
          <strong>{formatEur(g.rest_rechnerisch_netto)}</strong> netto noch zu stellen
          {gg.offene_positionen > 0 && (
            <span className="vg-doku-hinweis">
              {" "}· {gg.offene_positionen} Position(en) noch nicht aufgemessen
            </span>
          )}
        </div>
        <button
          type="button"
          className="vg-btn vg-btn-primary"
          disabled={!hatZeilen || entwurf.isPending}
          onClick={() => entwurf.mutate()}
        >
          {entwurf.isPending ? "Erzeuge …" : "Schlussrechnungs-Entwurf erzeugen"}
        </button>
      </div>
      {entwurf.isError && <div className="vg-banner is-warn">{errorText(entwurf.error)}</div>}
      {entwurf.isSuccess && (
        <div className="vg-banner is-success">
          Entwurf mit {entwurf.data.positionen} Position(en) angelegt
          {entwurf.data.nachtraege > 0 && `, davon ${entwurf.data.nachtraege} Nachträge`}. Er steht
          unter „Angebote &amp; Rechnungen" und hat noch keine Nummer — die entsteht beim Festschreiben.
        </div>
      )}
      <div className="vg-detail-section-hint" style={{ marginTop: 10, fontStyle: "italic" }}>
        {data.hinweis}
      </div>
    </div>
  );
}

/**
 * Eine Zeile der Gegenüberstellung.
 *
 * Der Wert wird beim Verlassen des Feldes gespeichert, nicht bei jedem Tastendruck:
 * „5" auf dem Weg zu „52" wäre sonst ein eigener, falscher Zwischenstand in der
 * Datenbank.
 */
function AufmassZeile({ zeile, speichern }: {
  zeile: {
    position_id: string; titel: string; einheit: string; einzelpreis_netto: number;
    angeboten_menge: number; angeboten_netto: number;
    abrechenbar_menge: number; abrechenbar_netto: number;
    differenz_menge: number; differenz_netto: number;
    aufgemessen: boolean; notiz: string; aus_angebot: boolean; belege: Lieferposition[];
    geliefert_menge?: number | null; geliefert_weicht_ab?: boolean;
  };
  speichern: (v: { position_id: string; menge: number | null; notiz?: string }) => void;
}) {
  const [menge, setMenge] = useState(zeile.aufgemessen ? mengeText(zeile.abrechenbar_menge) : "");
  const [notiz, setNotiz] = useState(zeile.notiz);
  const abweichung = zeile.aufgemessen && zeile.differenz_menge !== 0;
  const geliefert = zeile.geliefert_menge ?? null;
  const weichtAb = geliefert !== null && !!zeile.geliefert_weicht_ab;
  // Was die Lieferung gegenüber dem jetzt Abrechenbaren mehr oder weniger ist.
  const lieferDelta = weichtAb ? geliefert - zeile.abrechenbar_menge : 0;

  function liefermengeUebernehmen() {
    if (geliefert === null) return;
    setMenge(mengeText(geliefert));
    speichern({ position_id: zeile.position_id, menge: geliefert, notiz: notiz || "laut Lieferschein" });
    if (!notiz) setNotiz("laut Lieferschein");
  }

  return (
    <tr className={[zeile.aufgemessen ? "" : "is-offen", weichtAb ? "is-lieferung" : ""].join(" ").trim()}>
      <td data-label="Position">
        {zeile.titel}
        {!zeile.aus_angebot && <span className="vg-proj-tab-count">ergänzt</span>}
        <span className="vg-doku-hinweis">
          {formatEur(zeile.einzelpreis_netto)} / {zeile.einheit || "Einheit"}
        </span>
        {/* Der Beleg steht IN der Zeile, nicht als Klotz daneben — sonst muss
            man im Kopf zuordnen, und genau das war die Handarbeit. */}
        {zeile.belege.length > 0 && geliefert === null && (
          <span className="vg-beleg-zeile">
            geliefert: {zeile.belege.map((b) => `${mengeText(b.menge)} ${b.einheit} ${b.material}`).join(" · ")}
          </span>
        )}
      </td>
      <td data-label="Angeboten">
        {mengeText(zeile.angeboten_menge)} {zeile.einheit}
        <span className="vg-doku-hinweis">{formatEur(zeile.angeboten_netto)}</span>
        {/* Geliefert steht direkt unter angeboten: der Vergleich, den man sonst
            mit Lieferschein und Angebot nebeneinander im Kopf macht. */}
        {geliefert !== null && (
          <span
            className={weichtAb ? "vg-geliefert is-ab" : "vg-geliefert"}
            title={zeile.belege.map((b) => `${mengeText(b.menge)} ${b.einheit} ${b.material}`).join(" · ")}
          >
            geliefert {mengeText(geliefert)} {zeile.einheit}
          </span>
        )}
      </td>
      <td data-label="Abrechenbar">
        <input
          className="vg-aufmass-feld"
          inputMode="decimal"
          value={menge}
          placeholder={mengeText(zeile.angeboten_menge)}
          onChange={(e) => setMenge(e.target.value)}
          onBlur={() => {
            const wert = alsZahl(menge);
            const alt = zeile.aufgemessen ? zeile.abrechenbar_menge : null;
            if (wert !== alt) speichern({ position_id: zeile.position_id, menge: wert, notiz });
          }}
          aria-label={`Abrechenbare Menge für ${zeile.titel}`}
        />
        <span className="vg-doku-hinweis">
          {zeile.aufgemessen ? formatEur(zeile.abrechenbar_netto) : "noch nicht aufgemessen"}
        </span>
      </td>
      <td data-label="Differenz" className={abweichung ? "vg-differenz" : ""}>
        {abweichung ? (
          <>
            {zeile.differenz_menge > 0 ? "+" : ""}{mengeText(zeile.differenz_menge)} {zeile.einheit}
            <span className="vg-doku-hinweis">
              {zeile.differenz_netto > 0 ? "+" : ""}{formatEur(zeile.differenz_netto)}
            </span>
          </>
        ) : weichtAb ? null : "—"}
        {weichtAb && (
          <span className="vg-liefer-abweichung">
            Lieferung {lieferDelta > 0 ? "+" : ""}{mengeText(lieferDelta)} {zeile.einheit}
            {" · "}{lieferDelta > 0 ? "+" : ""}{formatEur(lieferDelta * zeile.einzelpreis_netto)}
            <button type="button" className="vg-btn vg-liefer-uebernehmen" onClick={liefermengeUebernehmen}>
              Liefermenge übernehmen
            </button>
          </span>
        )}
      </td>
      <td data-label="Notiz">
        <input
          className="vg-aufmass-notiz"
          value={notiz}
          placeholder="warum abweichend"
          onChange={(e) => setNotiz(e.target.value)}
          onBlur={() => {
            if (notiz !== zeile.notiz && zeile.aufgemessen) {
              speichern({ position_id: zeile.position_id, menge: zeile.abrechenbar_menge, notiz });
            }
          }}
          aria-label={`Notiz zu ${zeile.titel}`}
        />
      </td>
    </tr>
  );
}

/**
 * Beauftragte Position ergänzen — für Angebote, die nur eine Summe tragen.
 *
 * AG0200 und AN0001 haben keine Positionszeilen; ohne diesen Weg gäbe es dort
 * gar keine Gegenüberstellung. Die Position steht danach bei den
 * Angebotspositionen, nicht bei den Nachträgen: beauftragt ist beauftragt.
 */
function PositionErgaenzen({ projektId }: { projektId: string }) {
  const ergaenzen = usePositionErgaenzen(projektId);
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ bezeichnung: "", menge: "", einheit: "", preis: "" });

  function speichern() {
    if (!form.bezeichnung.trim()) return;
    ergaenzen.mutate(
      {
        bezeichnung: form.bezeichnung.trim(),
        menge: alsZahl(form.menge) ?? 0,
        einheit: form.einheit.trim(),
        einzelpreis_netto: alsZahl(form.preis) ?? 0,
      },
      { onSuccess: () => setForm({ bezeichnung: "", menge: "", einheit: "", preis: "" }) },
    );
  }

  if (!offen) {
    return (
      <button type="button" className="vg-btn" style={{ marginTop: 8 }} onClick={() => setOffen(true)}>
        + Beauftragte Position ergänzen
      </button>
    );
  }
  return (
    <div className="vg-akte-form" style={{ marginTop: 8 }}>
      <label className="is-breit">
        Beauftragte Leistung
        <input value={form.bezeichnung} autoFocus
          onChange={(e) => setForm((f) => ({ ...f, bezeichnung: e.target.value }))} />
      </label>
      <label>Menge
        <input inputMode="decimal" value={form.menge}
          onChange={(e) => setForm((f) => ({ ...f, menge: e.target.value }))} />
      </label>
      <label>Einheit
        <input value={form.einheit} placeholder="lfm, Stk, m²"
          onChange={(e) => setForm((f) => ({ ...f, einheit: e.target.value }))} />
      </label>
      <label>Einzelpreis netto
        <input inputMode="decimal" value={form.preis}
          onChange={(e) => setForm((f) => ({ ...f, preis: e.target.value }))} />
      </label>
      <div className="vg-akte-form-actions">
        {ergaenzen.isError && <span>{errorText(ergaenzen.error)}</span>}
        <button type="button" className="vg-btn" onClick={() => setOffen(false)}>Fertig</button>
        <button type="button" className="vg-btn vg-btn-primary"
          disabled={!form.bezeichnung.trim() || ergaenzen.isPending} onClick={speichern}>
          {ergaenzen.isPending ? "Legt an …" : "Position übernehmen"}
        </button>
      </div>
    </div>
  );
}

/** Was unterwegs dazukam — sichtbar getrennt vom Angebot. */
function Nachtraege({ projektId, liste }: {
  projektId: string;
  liste: { id: string; titel: string; einheit: string; menge: number; einzelpreis_netto: number; netto: number; notiz: string }[];
}) {
  const anlegen = useNachtragAnlegen(projektId);
  const loeschen = useNachtragLoeschen(projektId);
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ bezeichnung: "", menge: "", einheit: "", preis: "" });

  function speichern() {
    if (!form.bezeichnung.trim()) return;
    anlegen.mutate(
      {
        bezeichnung: form.bezeichnung.trim(),
        menge: alsZahl(form.menge) ?? 0,
        einheit: form.einheit.trim(),
        einzelpreis_netto: alsZahl(form.preis) ?? 0,
      },
      { onSuccess: () => { setForm({ bezeichnung: "", menge: "", einheit: "", preis: "" }); setOffen(false); } },
    );
  }

  return (
    <div className="vg-doku-gruppe">
      <div className="vg-doku-gruppe-titel">
        Nachträge <span className="vg-proj-tab-count">{liste.length}</span>
        <button type="button" className="vg-btn" onClick={() => setOffen((z) => !z)} style={{ marginLeft: "auto" }}>
          {offen ? "Abbrechen" : "+ Nachtrag"}
        </button>
      </div>
      <small className="vg-doku-hinweis">
        Stand nicht im Angebot. Getrennt gehalten, damit im Kundengespräch klar bleibt, was beauftragt war.
      </small>

      {offen && (
        <div className="vg-akte-form" style={{ marginTop: 8 }}>
          <label className="is-breit">
            Leistung
            <input value={form.bezeichnung} autoFocus
              onChange={(e) => setForm((f) => ({ ...f, bezeichnung: e.target.value }))} />
          </label>
          <label>Menge
            <input inputMode="decimal" value={form.menge}
              onChange={(e) => setForm((f) => ({ ...f, menge: e.target.value }))} />
          </label>
          <label>Einheit
            <input value={form.einheit} placeholder="lfm, Stk, m²"
              onChange={(e) => setForm((f) => ({ ...f, einheit: e.target.value }))} />
          </label>
          <label>Einzelpreis netto
            <input inputMode="decimal" value={form.preis}
              onChange={(e) => setForm((f) => ({ ...f, preis: e.target.value }))} />
          </label>
          <div className="vg-akte-form-actions">
            {anlegen.isError && <span>{errorText(anlegen.error)}</span>}
            <button type="button" className="vg-btn vg-btn-primary"
              disabled={!form.bezeichnung.trim() || anlegen.isPending} onClick={speichern}>
              {anlegen.isPending ? "Legt an …" : "Nachtrag übernehmen"}
            </button>
          </div>
        </div>
      )}

      {liste.length > 0 && (
        <table className="k-cardtable" style={{ marginTop: 8 }}>
          <thead><tr><th>Leistung</th><th>Menge</th><th>Netto</th><th /></tr></thead>
          <tbody>
            {liste.map((n) => (
              <tr key={n.id}>
                <td data-label="Leistung">{n.titel}</td>
                <td data-label="Menge">{mengeText(n.menge)} {n.einheit}</td>
                <td data-label="Netto">{formatEur(n.netto)}</td>
                <td data-label="">
                  <button type="button" className="vg-btn" disabled={loeschen.isPending}
                    onClick={() => loeschen.mutate(n.id)}>Entfernen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
